import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getAdminUserList } from '@/lib/coloc-api/admin';
import { Button } from '@/components/design-system/atoms/button';
import { useCreateThreadMessage } from '../../hooks/use-create-thread-message';
import { LoadingSpinner } from '@/components/design-system/atoms/loading-spinner';
import { useCreateDiscussionThread } from '../../hooks/use-create-discussion-thread';
import { useDiscussionThreadProviderContext } from '../../hooks/use-discussion-thread';
import { Mention, MentionsInput } from 'react-mentions';
import { PropertyManager } from '@/features/admin/contracts/types';
import { MentionSuggestion } from './MentionSuggestion';
import defaultStyle from './defaultStyle';
import defaultMentionStyle from './defaultMentionStyle';

const DiscussionThreadInput = () => {
  const [adminUsers, setAdminUsers] = useState<
    Array<PropertyManager>
  >([]);
  const [calculatedStyle, setCalculatedStyle] = useState(defaultStyle);

  const { mutate: createThreadMessage } = useCreateThreadMessage();
  const { mutate: createDiscussionThread } = useCreateDiscussionThread();
  const { discussionThreadHasBeenCreated } =
    useDiscussionThreadProviderContext()!;

  const containerRef = useRef<HTMLDivElement>(null);

  const [value, setValue] = useState({
    textInputValue: '',
    mention_ids: [],
  });

  const { textInputValue, mention_ids } = value;

  const isInputContainsText = useMemo(() => {
    return textInputValue.trim().length >= 1;
  }, [textInputValue]);

  useEffect(() => {
    getAdminUserList().then((response) => {
      setAdminUsers(response.data.data);
    });
  }, []);

  const users = useMemo(() => {
    return adminUsers.map((adminUser) => ({
      id: adminUser.id,
      display: `${adminUser.first_name} ${adminUser.last_name}`,
      avatar_url: adminUser.avatar_url,
    }));
  }, [adminUsers]);


  const onChange = (e: any, newValue: string, newPlainTextValue: string, mentions: any) => {
    setValue({
      textInputValue: newValue,
      mention_ids: mentions.map((mention: any) => mention.id),
    });
  };

  useEffect(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const { left, right, height } = rect;

      setCalculatedStyle({
        ...defaultStyle,
        suggestions: {
          ...defaultStyle.suggestions,
          top: null,
          bottom: height - 20,
          left: 10,
          width: right - left - 20,
        },
      });
    }
  }, [textInputValue]);

  return (
    <div
      ref={containerRef}
      className="relative -mx-5 border-t border-solid border-gray-200 p-5 pt-4"
    >
      <div className="absolute right-[1px] z-10">
        {createThreadMessage.isLoading || createDiscussionThread.isLoading ? (
          <LoadingSpinner size={24} />
        ) : (
          <Button
            type="button"
            variant="icon"
            iconName="send-2"
            disabled={!isInputContainsText}
            onClick={() => {
              if (isInputContainsText) {
                if (discussionThreadHasBeenCreated) {
                  createThreadMessage.mutate({
                    content: textInputValue,
                    mention_ids,
                  });
                } else {
                  createDiscussionThread.mutate({
                    content: textInputValue,
                    mention_ids,
                  });
                }
                setValue({
                  textInputValue: '',
                  mentions: [],
                });
              }
            }}
          />
        )}
      </div>

      <MentionsInput
        value={textInputValue}
        onChange={onChange}
        style={calculatedStyle}
        placeholder="Write your note"
        a11ySuggestionsListLabel="Suggested mentions"
      >
        <Mention
          data={users}
          style={defaultMentionStyle}
          renderSuggestion={MentionSuggestion}
          markup="[@__display__](mention:__id__)"
        />
      </MentionsInput>


    </div>
  );
};

export { DiscussionThreadInput };
